import { ButtonHTMLAttributes, FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';

import styles from './VerticalTabButtonGroup.module.scss';
import { Link } from 'routes';
import { isMobile } from 'utils/layout';

const TRANSLATION = {
  en: {
    expand: ' can be expanded',
    collapse: ' can be collapsed',
  },
  ja: {
    expand: 'をすべて表示',
    collapse: 'の一部を非表示',
  },
};

const EXPANDABLE_CONTAINER_HEIGHT = 84;

interface Item {
  id: string;
  label: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
}

interface Props {
  areVerticalsCollapsed: boolean;
  background: boolean;
  items: Array<Item>;
  itemLengthToCollapse: number;
  language: string;
  onItemClick?: (itemId: string) => void;
  selectVertical: (vertical: string) => void;
  selectedC2TabName: string;
  selectedItem: string;
  setAreVerticalsCollapsed: (areVerticalsCollapsed: boolean) => void;
  slug: string;
}

export const VerticalTabButtonGroup: FC<Props> = ({
  areVerticalsCollapsed,
  setAreVerticalsCollapsed,
  background,
  items,
  itemLengthToCollapse,
  language,
  slug,
  onItemClick = () => undefined,
  selectedC2TabName,
  selectedItem,
  selectVertical,
}) => {
  // scroll to category-2 tab when vertical is collapse/expanded
  const handleItemClick = () => {
    setAreVerticalsCollapsed(!areVerticalsCollapsed);
    const tabNavigation = document.querySelector<HTMLElement>(
      // direct parent of <VerticalTabButtonGroup> component
      `div:has(>.${styles['z-vertical-tab-button-group']})`
    );
    if (tabNavigation && isMobile(window.screen.width)) {
      tabNavigation.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // set initial state of showExpand based on the length of items
  const [showExpand, setShowExpand] = useState(items.length >= itemLengthToCollapse);
  useEffect(() => {
    const container = document.querySelector<HTMLElement>(`.${styles['item-list']}`) ?? {
      scrollHeight: 0,
    };
    // on loaded: set showExpand based on the height of container
    setShowExpand(container.scrollHeight > EXPANDABLE_CONTAINER_HEIGHT);
  }, [selectedC2TabName]);

  const lang = language === 'en' ? 'en' : 'ja';

  return (
    <div
      className={classNames(styles['z-vertical-tab-button-group'], {
        [styles['background']]: background,
      })}
    >
      <AnimateHeight
        duration={300}
        height={areVerticalsCollapsed ? EXPANDABLE_CONTAINER_HEIGHT : 'auto'}
      >
        {items.length > 0 && (
          <div className={styles['item-list']}>
            {items.map((item) => (
              // eslint-disable-next-line jsx-a11y/interactive-supports-focus
              <span
                role="button"
                className={styles['pill']}
                key={`vtbg_item_${item.id}`}
                tabIndex={0}
                onClick={() => {
                  onItemClick(item.id);
                  setAreVerticalsCollapsed(true);
                  selectVertical(item.id);
                }}
              >
                <div
                  className={classNames({
                    [styles['pill-selected']]: item.id === selectedItem,
                    [styles['pill-unselected']]: item.id !== selectedItem,
                  })}
                >
                  <Link
                    prefetch={false}
                    route={`${window.location.origin}${
                      language === 'en' ? '/en' : ''
                    }/profile/${slug}/pro${item.id ? `/${item.id}` : ''}`}
                  >
                    <a className={`${styles['pill-label']}`} data-cy={'popular-vertical'}>
                      {item.label}
                    </a>
                  </Link>
                </div>
              </span>
            ))}
          </div>
        )}
      </AnimateHeight>
      {areVerticalsCollapsed && showExpand && (
        <div
          className={classNames(styles['expand-toggle'], styles['expand-toggle-closed'])}
          onClick={handleItemClick}
          role="presentation"
        >
          <span>
            {selectedC2TabName}
            {TRANSLATION[lang]['expand']}
          </span>
        </div>
      )}
      {!areVerticalsCollapsed && showExpand && (
        <div
          className={classNames(styles['expand-toggle'], styles['expand-toggle-open'])}
          onClick={handleItemClick}
          role="presentation"
        >
          <span>
            {selectedC2TabName}
            {TRANSLATION[lang]['collapse']}
          </span>
        </div>
      )}
    </div>
  );
};

export default VerticalTabButtonGroup;
export type { Props as VerticalTabButtonGroupProps };
